"use client";

import { useRef } from "react";
import { Sparkles, ArrowRight, ShieldCheck, CheckCircle2, Search, Bot } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
if (typeof window !== "undefined") {
  gsap.registerPlugin(useGSAP);
}
const aiFeatures = [{
  title: "AI search visibility audits"
}, {
  title: "ChatGPT SEO and Google AI Overviews / AI Mode"
}, {
  title: "Content and entity clarity for AI discovery"
}, {
  title: "Technical access for AI crawlers and structured data"
}];
const illustrativeWorkflow = [{
  platform: "ChatGPT Search",
  task: "Sample buyer prompts, note cited sources",
  action: "Sampled"
}, {
  platform: "Google AI Overviews and AI Mode",
  task: "Check which pages and sources are referenced",
  action: "Checked"
}, {
  platform: "Perplexity",
  task: "Compare cited sources with competitors",
  action: "Compared"
}, {
  platform: "Copilot and Gemini",
  task: "Review brand description accuracy",
  action: "Reviewed"
}];
export default function Aisearch() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const isReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isReduced) return;
    const tl = gsap.timeline({
      defaults: {
        ease: "power3.out"
      }
    });
    tl.from(".gsap-badge", {
      opacity: 0,
      y: -12,
      duration: 0.5
    }).from(".gsap-heading", {
      opacity: 0,
      y: 20,
      duration: 0.7
    }, "-=0.3").from(".gsap-subtext", {
      opacity: 0,
      y: 16,
      duration: 0.6
    }, "-=0.4").from(".gsap-features", {
      opacity: 0,
      y: 16,
      duration: 0.6
    }, "-=0.4").from(".gsap-actions", {
      opacity: 0,
      scale: 0.95,
      duration: 0.5
    }, "-=0.3").from(".gsap-card", {
      opacity: 0,
      x: 24,
      duration: 0.8
    }, "-=0.6");
  }, {
    scope: containerRef
  });
  return <section id="heroaisearchoptimization" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28 text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--color-secondary)_0%,_transparent_70%)] opacity-30" />
      
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <div className="lg:col-span-7 flex flex-col items-start space-y-6 text-left">
            
            <div className="gsap-badge inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium tracking-wide">
                <Sparkles className="size-3.5 text-accent" />
                <span>AI Search Consultancy</span>
              </Badge>
            </div>

            <h1 className="gsap-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-default leading-[1.15]">
              AI Search Optimisation
            </h1>

            <p className="gsap-subtext text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl font-normal leading-relaxed">
              Your buyers are already asking ChatGPT, Google AI Overviews, Perplexity and Bing Copilot to research suppliers and shortlist services. I&apos;m Yassine Essaadi, an independent AI SEO consultant, and I help UK SMEs, B2B companies and ecommerce brands make their existing content, authority and technical foundations easier for AI systems to find, understand and reference — without starting your SEO from scratch.
            </p>

            <div className="gsap-features bg-card border border-border rounded-xl p-4 sm:p-5 shadow-xs w-full max-w-2xl">
              <div className="flex items-center gap-3 mb-3 pb-3 border-b border-border">
                <div className="relative shrink-0">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Independent SEO Consultant" loading="lazy" className="size-11 rounded-full object-cover border border-primary/20" />
                  <span className="absolute bottom-0 right-0 size-3 bg-primary rounded-full border-2 border-card" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground font-default">
                    Independent SEO consultant: Yassine Essaadi
                  </p>
                  <p className="text-xs text-muted-foreground">
                    AI search audits • Content and technical optimisation • Transparent reporting
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {aiFeatures.map((item, i) => <div data-index={i} key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="text-xs sm:text-sm font-medium text-foreground">{item.title}</span>
                  </div>)}
              </div>
            </div>

            <div className="gsap-actions flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full sm:w-auto pt-2">
              <Button size="lg" className="px-6 py-6 text-base font-medium shadow-md cursor-pointer" asChild>
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" className="px-6 py-6 text-base font-medium cursor-pointer" asChild>
                <Link to="/SeoAuditsPage">
                  <span>Get a Free SEO Audit</span>
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
              <ShieldCheck className="size-4 text-primary" />
              <span>Tailored strategy • Transparent reporting • Practical implementation</span>
            </div>

          </div>

          <div className="lg:col-span-5 w-full">
            <div className="gsap-card bg-card border border-border rounded-2xl p-6 shadow-xl relative overflow-hidden">
              
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <Bot className="size-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground font-default">
                      Illustrative audit workflow
                    </p>
                    <p className="text-xs text-muted-foreground">How I sample AI answers</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs gap-1 border-muted-foreground/30 text-muted-foreground">
                  <span>Illustration, not client results</span>
                </Badge>
              </div>

              <div className="space-y-3.5 mb-6">
                {illustrativeWorkflow.map((step, i) => <div data-index={i} key={i} className="p-3.5 rounded-xl bg-background border border-border/80 flex items-center justify-between shadow-2xs hover:border-primary/40 transition-colors">
                    <div className="flex items-center gap-3 min-w-0 pr-2">
                      <div className="size-8 rounded-md bg-muted flex items-center justify-center text-foreground font-bold text-xs shrink-0">
                        <Search className="size-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs sm:text-sm font-semibold text-foreground font-default truncate">
                          {step.platform}
                        </div>
                        <div className="text-[13px] text-muted-foreground leading-snug">
                          {step.task}
                        </div>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div className="text-xs font-medium text-muted-foreground px-2 py-0.5 rounded bg-muted/60">
                        {step.action}
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="bg-muted/70 rounded-xl p-4 border border-border/60 flex items-start gap-3">
                <p className="text-xs text-foreground/90 leading-relaxed">
                  <strong className="font-semibold text-foreground">Why this matters:</strong> AI answers vary between sessions and platforms, so I sample repeatedly and report trends rather than promises.
                </p>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>;
}